import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader, Circle } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const STATUS_STYLE = {
  CERTIFIED: ["text-emerald-400 border-emerald-500/40", Check],
  "TRAINING REQUIRED": ["text-[#E4C987] border-[#C9A45C]/40", Loader],
  "TRAINING IN PROGRESS": ["text-[#E4C987] border-[#C9A45C]/40", Loader],
  "ASSESSMENT PENDING": ["text-blue-300 border-blue-400/40", Loader],
  "NOT APPLIED": ["text-gray-400 border-white/15", Circle],
  SUSPENDED: ["text-red-400 border-red-500/40", Circle],
  EXPIRED: ["text-red-400 border-red-500/40", Circle],
};

export default function ApplicatorServices() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [svcs, setSvcs] = useState(null);

  useEffect(() => { api.get("/applicator/services").then(({ data }) => setSvcs(data)).catch(() => {}); }, []);
  if (!svcs) return <div className="text-gray-500">Loading…</div>;

  const Row = ({ s }) => {
    const [cls, Icon] = STATUS_STYLE[s.cert_status] || STATUS_STYLE["NOT APPLIED"];
    return (
      <div className="hair-border bg-white/[0.02] p-4 flex items-center justify-between" data-testid={`svc-row-${s.service_id}`}>
        <div><p className="text-sm">{s.service_name}</p><p className="text-xs text-gray-500">{s.category}</p></div>
        <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 border ${cls}`}><Icon size={13} /> {s.cert_status}</span>
      </div>
    );
  };

  const Group = ({ title, items, testid }) => (
    <div className="mb-8" data-testid={testid}>
      <p className="errah-label gold-text mb-3">{title} ({items.length})</p>
      {items.length ? <div className="space-y-2">{items.map((s) => <Row key={s.id} s={s} />)}</div> : <p className="text-sm text-gray-600">—</p>}
    </div>
  );

  const certified = svcs.all.filter((s) => s.cert_status === "CERTIFIED");
  const inProgress = svcs.all.filter((s) => ["TRAINING REQUIRED", "TRAINING IN PROGRESS", "ASSESSMENT PENDING"].includes(s.cert_status));
  const notCert = svcs.all.filter((s) => ["NOT APPLIED", "SUSPENDED", "EXPIRED"].includes(s.cert_status));

  return (
    <div data-testid="applicator-services-page">
      <h1 className="serif-head text-3xl lg:text-4xl mb-6">{t("my_services")}</h1>
      <Group title={t("certified")} items={certified} testid="grp-certified" />
      <Group title={t("training")} items={inProgress} testid="grp-training" />
      <Group title={t("not_certified")} items={notCert} testid="grp-not-certified" />
      <button onClick={() => navigate("/applicator/training")} data-testid="get-certified-more" className="bg-[#C9A45C] text-black px-6 py-3.5 text-sm font-semibold hover:bg-[#E4C987] transition-colors">{t("get_certified_more")}</button>
    </div>
  );
}
